import type { WorkerClientMetadata } from './envelope';
import type { WorkerStateKey } from './stateRegistry';
/*
 * The contract pair (ADR 0016): types plus a value-level manifest, importable by both the main
 * bundle and the worker bundle without dragging worker code into either. A worker's contract
 * file must contain types and its manifest only — a value-level import of worker code from the
 * main bundle is a silent bundle merge under Vite's worker bundling.
 */
import type { ZodType } from 'zod';

export interface WorkerContract {
    requests: { [type: string]: { request: unknown; response: unknown } };
    events: { [type: string]: unknown };
}

export type WorkerRequestType<C extends WorkerContract> = keyof C['requests'] & string;
export type WorkerEventType<C extends WorkerContract> = keyof C['events'] & string;

/** Request/response/event schemas for a `zod`-codec contract, shared by both sides. */
export interface WorkerContractSchemas<C extends WorkerContract> {
    readonly requests: {
        readonly [K in keyof C['requests']]: {
            readonly request: ZodType<C['requests'][K]['request']>;
            readonly response: ZodType<C['requests'][K]['response']>;
        };
    };
    readonly events: { readonly [K in keyof C['events']]: ZodType<C['events'][K]> };
}

interface WorkerManifestBase {
    /** State keys the worker composes; exactly these are gathered into `init.state` and pushed. */
    readonly state: readonly WorkerStateKey[];
}

/** Payloads pass through `postMessage` untouched — zero serialization overhead. */
export interface StructuredCloneWorkerManifest extends WorkerManifestBase {
    readonly codec: 'structuredClone';
}

/**
 * Payloads encode/decode through the contract's schemas — required wherever payloads carry
 * types structured clone can't round-trip or where cross-boundary validation matters.
 */
export interface ZodWorkerManifest<C extends WorkerContract> extends WorkerManifestBase {
    readonly codec: 'zod';
    readonly schemas: WorkerContractSchemas<C>;
}

export type WorkerManifest<C extends WorkerContract> = StructuredCloneWorkerManifest | ZodWorkerManifest<C>;

export const defineWorkerManifest = <C extends WorkerContract>(manifest: WorkerManifest<C>): WorkerManifest<C> =>
    manifest;

export interface WorkerClientContext {
    readonly clientId: string;
    /** From `init.client`: datadog session id and tab identity — the trading worker's per-port context relay, generalized. */
    readonly metadata: WorkerClientMetadata;
}

export interface WorkerChannel<C extends WorkerContract> {
    broadcast<K extends WorkerEventType<C>>(type: K, payload: C['events'][K]): void;
    sendTo<K extends WorkerEventType<C>>(clientId: string, type: K, payload: C['events'][K]): void;
}

/**
 * A worker implementation: an injectable constructed from the composed worker container, seeing
 * container, channel, and clients — never `self`, `onmessage`, or ports. `client` is inert for
 * dedicated workers (exactly one client); multi-client workers need it.
 */
export interface WorkerImpl<C extends WorkerContract> {
    handleRequest<K extends WorkerRequestType<C>>(
        type: K,
        payload: C['requests'][K]['request'],
        client: WorkerClientContext,
    ): Promise<C['requests'][K]['response']>;

    onClientConnected?(client: WorkerClientContext): void;
    onClientDisconnected?(client: WorkerClientContext): void;
}
